import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2, Paperclip } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    listEmailAccounts,
    sendEmail,
    fetchQuotePdfBlob,
} from "../../lib/api";

// Compose-dialog that emails the current quote (with PDF auto-attached)
// through the user's connected IMAP/SMTP email account (iter-102).
//
// Props:
//   estimateId   string  — the saved estimate id (used for the backend PDF endpoint + filename)
//   buildPayload () => quote_data — function that returns the normalised payload (so it's
//                   built from the current QuoteView state at dialog-open time)
//   defaultEmail string  — customer email pre-fill
//   defaultName  string  — customer name pre-fill (used in subject + body)
//   companyName  string  — contractor's company name (used in subject + signature)
//   onClose      () => void
export function SendQuoteEmailDialog({
    estimateId,
    buildPayload,
    defaultEmail,
    defaultName,
    companyName,
    onClose,
}) {
    const [accounts, setAccounts] = useState([]);
    const [loadingAccts, setLoadingAccts] = useState(true);
    const [accountId, setAccountId] = useState("");
    const [to, setTo] = useState(defaultEmail || "");
    const [cc, setCc] = useState("");
    const [subject, setSubject] = useState(
        `${companyName ? companyName + " — " : ""}Quote${defaultName ? " for " + defaultName : ""}`,
    );
    const [body, setBody] = useState(
        `Hi${defaultName ? " " + defaultName : ""},\n\n`
        + `Please find the attached quote.\n\n`
        + `Let me know if you have any questions or want to discuss adjustments.\n\n`
        + `Thanks,\n${companyName || ""}`,
    );
    const [sending, setSending] = useState(false);
    const [pdfReady, setPdfReady] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfErr, setPdfErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const rows = await listEmailAccounts();
                if (cancelled) return;
                setAccounts(rows || []);
                if (rows?.length) setAccountId(rows[0].id);
            } catch {
                if (!cancelled) setAccounts([]);
            } finally {
                if (!cancelled) setLoadingAccts(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Prepare the PDF in the background so Send is instant.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setPdfReady(false);
            setPdfErr("");
            try {
                const blob = await fetchQuotePdfBlob(estimateId, buildPayload());
                if (!cancelled) {
                    setPdfBlob(blob);
                    setPdfReady(true);
                }
            } catch (err) {
                if (!cancelled) setPdfErr(err?.response?.data?.detail || err?.message || "Couldn't build PDF");
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimateId]);

    const handleSend = async () => {
        if (!accountId) { toast.error("Pick an email account"); return; }
        const toEmail = (to || "").trim();
        if (!toEmail || !toEmail.includes("@")) {
            toast.error("Enter a valid recipient email");
            return;
        }
        if (!pdfReady || !pdfBlob) {
            toast.error("Quote PDF still building — try again in a moment");
            return;
        }
        setSending(true);
        try {
            const fname = `quote-${(estimateId || "").slice(0, 8)}.pdf`;
            const pdfFile = new File([pdfBlob], fname, { type: "application/pdf" });
            const ccList = (cc || "")
                .split(",")
                .map((s) => s.trim())
                .filter((s) => s.includes("@"));
            await sendEmail(
                accountId,
                {
                    to: [toEmail],
                    cc: ccList,
                    subject,
                    body_text: body,
                },
                [pdfFile],
            );
            toast.success("Quote emailed");
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent
                className="bg-[#111] border border-white/15 text-slate-200 max-w-2xl"
                data-testid="quote-send-email-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <Mail className="w-4 h-4 text-[#8fa4f0]" /> Send quote via email
                    </DialogTitle>
                </DialogHeader>

                {loadingAccts ? (
                    <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading accounts…
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
                        No email accounts connected yet.{" "}
                        <a href="/email" className="font-bold text-amber-300 underline">
                            Add one from the Email page
                        </a>{" "}
                        first.
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                From account
                            </label>
                            <select
                                value={accountId}
                                onChange={(e) => setAccountId(e.target.value)}
                                data-testid="quote-send-email-account"
                                className="mt-1 w-full bg-[#0a0a0a] border border-white/15 rounded-sm px-3 py-2 text-sm"
                            >
                                {accounts.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.display_name ? `${a.display_name} <${a.email_address}>` : a.email_address}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">To</label>
                                <input
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    placeholder="customer@example.com"
                                    data-testid="quote-send-email-to"
                                    className="mt-1 w-full bg-[#0a0a0a] border border-white/15 rounded-sm px-3 py-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Cc (optional)</label>
                                <input
                                    value={cc}
                                    onChange={(e) => setCc(e.target.value)}
                                    placeholder="comma separated"
                                    data-testid="quote-send-email-cc"
                                    className="mt-1 w-full bg-[#0a0a0a] border border-white/15 rounded-sm px-3 py-2 text-sm"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Subject</label>
                            <input
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                data-testid="quote-send-email-subject"
                                className="mt-1 w-full bg-[#0a0a0a] border border-white/15 rounded-sm px-3 py-2 text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Message</label>
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                rows={6}
                                data-testid="quote-send-email-body"
                                className="mt-1 w-full bg-[#0a0a0a] border border-white/15 rounded-sm px-3 py-2 text-sm"
                            />
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                            <Paperclip className="w-3.5 h-3.5" />
                            {pdfErr ? (
                                <span className="text-rose-400" data-testid="quote-send-email-pdf-err">
                                    PDF build failed: {pdfErr}
                                </span>
                            ) : pdfReady ? (
                                <span className="text-emerald-400" data-testid="quote-send-email-pdf-ready">
                                    quote.pdf ready to attach
                                </span>
                            ) : (
                                <span className="text-slate-500 inline-flex items-center gap-1">
                                    <Loader2 className="w-3 h-3 animate-spin" /> building quote.pdf…
                                </span>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="quote-send-email-cancel"
                                className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border border-white/15 text-slate-300 hover:text-white hover:bg-white/10"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={sending || !pdfReady}
                                data-testid="quote-send-email-send"
                                className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
                            >
                                {sending ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                    <Send className="w-3 h-3" />
                                )}
                                Send
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
